import { useState, useCallback, useEffect } from 'react';
import type { AuditLog, AuditEntityType } from '@byte-store-control/shared';
import {
  getStoreAuditLogs,
  getMyActivity,
  getAllAuditLogs,
  getAuditLogsByType,
  type AuditQueryOptions,
} from '../api/audit';

export interface UseAuditLogsOptions {
  storeNumber?: string;
  isMyActivity?: boolean;
  entityType?: AuditEntityType;
  startDate?: string;
  endDate?: string;
  limit?: number;
  autoFetch?: boolean;
}

export interface UseAuditLogsReturn {
  logs: AuditLog[];
  isLoading: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  refresh: () => Promise<void>;
  totalCount: number;
  /** True if the last response from the backend skipped one or more stores due to timeouts. */
  partial: boolean;
  /** Number of stores skipped in the last response due to timeout/error. */
  failedStoreCount: number;
}

const DEFAULT_LIMIT = 50;

export function useAuditLogs(options: UseAuditLogsOptions = {}): UseAuditLogsReturn {
  const {
    storeNumber,
    isMyActivity = false,
    entityType,
    startDate,
    endDate,
    limit = DEFAULT_LIMIT,
    autoFetch = true,
  } = options;

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [partial, setPartial] = useState(false);
  const [failedStoreCount, setFailedStoreCount] = useState(0);

  const fetchLogs = useCallback(
    async (isLoadMore = false) => {
      setIsLoading(true);
      setError(null);

      try {
        const queryOptions: AuditQueryOptions = {
          limit,
          startDate,
          endDate,
          startKey: isLoadMore && nextKey ? nextKey : undefined,
        };

        let response;

        if (isMyActivity) {
          // Fetch current user's activity
          response = await getMyActivity(queryOptions);
        } else if (storeNumber) {
          // Fetch logs for a specific store
          response = await getStoreAuditLogs(storeNumber, queryOptions);
        } else if (entityType) {
          // Fetch logs by entity type
          response = await getAuditLogsByType(entityType, queryOptions);
        } else {
          // Fetch all logs for accessible stores
          response = await getAllAuditLogs(queryOptions);
        }

        if (isLoadMore) {
          setLogs((prev) => [...prev, ...response.logs]);
        } else {
          setLogs(response.logs);
        }

        setNextKey(response.nextKey);
        setTotalCount((prev) => (isLoadMore ? prev : response.count));
        setPartial(Boolean(response.partial));
        setFailedStoreCount(response.failedStoreCount ?? 0);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch audit logs';
        setError(message);
        console.error('Failed to fetch audit logs:', err);
        // On Load More failure, keep the existing logs visible rather than wiping them.
      } finally {
        setIsLoading(false);
      }
    },
    [storeNumber, isMyActivity, entityType, startDate, endDate, limit, nextKey]
  );

  const loadMore = useCallback(async () => {
    if (!isLoading && nextKey) {
      await fetchLogs(true);
    }
  }, [isLoading, nextKey, fetchLogs]);

  const refresh = useCallback(async () => {
    setNextKey(null);
    setLogs([]);
    await fetchLogs(false);
  }, [fetchLogs]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch) {
      setNextKey(null);
      setLogs([]);
      fetchLogs(false);
    }
  }, [storeNumber, isMyActivity, entityType, startDate, endDate, autoFetch]);

  return {
    logs,
    isLoading,
    error,
    loadMore,
    hasMore: Boolean(nextKey),
    refresh,
    totalCount,
    partial,
    failedStoreCount,
  };
}
